// Clear words (CLEAR_WORDS=1): one small vocabulary on every member screen, so a brand new person can tell a lesson from a small step from a practice.
// Five nouns she learns: lesson (anything she opens to learn, a quest is a lesson with a Play), small step (anything she does), practice (the optional things,
// a challenge is a 7 day one), channel, course. The founder's words stay: course, achievement (was benchmark, was mark), level, channel, lesson, small step.
// Retired from the chrome under the flag: quest as a noun, Paths and path as a container, pillar, mark and benchmark for an achievement, Daily recall, Bi-weekly, My journey, item.
// Only chrome changes: tab names, screen titles, container names, buttons, toasts, locked reasons. No lesson, quest, practice or challenge CONTENT is touched,
// and no id, route, score or review id of approved content moves. Each screen asks cw(key, fallback) and reads the fallback, byte for byte, while the flag is off.
// Reviewed whole on the staff review page (scripts/clear-words-review.mjs, group "Clear words", id clearwords.<hash8>). Change a word and the id changes.
// Rules: grade 5 at most, sentences of 15 words or fewer, no dashes of any kind, no semicolons, nothing that shames or hurries, no health claims,
// never program, training, 90 days, phase or week n, never a total of lessons. Holes like {name} are filled by the screen.

/** key to the word she reads with the flag on. The member app imports only this object, so the review table below never rides in app.js. */
export const CLEAR_WORDS = Object.freeze({
  tabLessons: 'Lessons',
  navCalm: 'Calm minute',
  navReview: 'Scan review',
  navProgress: 'Your progress',
  chanLine: 'More to read and do. More opens as you finish lessons.',
  moreRow: 'Your progress: numbers, quizzes and your week',
  lessonsH1: 'Your lessons',
  lessonsSub: 'Every lesson, by topic. Each one opens when you finish the one before it.',
  byTopic: 'By topic',
  progressRowSub: 'Your achievements, your numbers, no end',
  practicesRow: 'Practices',
  practicesRowSub: 'Short practices, and 7 day challenges. All optional.',
  sevenDay: '7 days of lessons',
  pathDone: 'All seven days done',
  meaningTile: 'Blueprint, sanctuary, progress',
  tryQuest: 'A lesson to play',
  startQuest: 'Start the lesson',
  pathCardHead: 'Six short lessons and a review day',
  startPath: 'Start day 1',
  continuePath: 'Continue',
  backToPillar: 'Back to the topic',
  pathLine: 'Six short lessons and a review day. About 10 minutes each.',
  questMin: 'Lesson {n} · {m} min',
  backToPaths: 'Back to Lessons',
  questOf: 'Lesson {n} of {of}',
  closeQuest: 'Close lesson',
  startFull: 'Start the lesson',
  startLite: 'Start the lighter lesson',
  swapToFull: 'Do the full lesson instead',
  complete: 'Lesson complete',
  upNextQuest: 'Up next · Lesson {n}: {title}',
  backToPath: 'Back to the list',
  nextQuest: 'Next lesson: {title}',
  doneQuest: 'Next lesson, tomorrow: {title}.',
  hlQuest: 'That is your {ord} lesson this week.',
  weekLearnOne: 'lesson explained',
  weekLearnMany: 'lessons explained',
  weekPracticeOne: 'small step tried',
  weekPracticeMany: 'small steps tried',
  explainedSmall: 'lessons explained',
  triedSmall: 'small steps tried',
  learnedNone: 'No lessons this week. No rush.',
  nextPaths: 'Pick any lesson from Lessons.',
  mapLater: 'Lessons for this topic come later. {n} are planned.',
  recapHead: 'Your week of lessons is ready.',
  doorQuest: 'A lesson to play',
  fQuests: 'Lessons played',
  fTried: 'Small steps tried',
  chQuests: 'Lessons on this channel',
  chChallenges: '7 day challenges',
  chChallengesSub: 'One small step a day, for seven days. Optional.',
  todayHere: 'Start here today',
  bedHead: 'A sound under your lessons',
  readAloudSub: 'A Read to me button on lesson screens.',
  soundsLine: 'Lessons can play soft water, wind and birds.',
  unlockLine: 'A lesson opened early for you: {title}.',
  journeyEye: 'Your progress',
  soFar: 'Your progress so far',
  pathNoEnd: 'This course has no end',
  marksHeading: 'Badges',
  ringLearn: 'A lesson or today’s question',
  ringRecall: 'One question from a past lesson',
  blueprintSub: 'Your plan on one page. It fills in as you build.',
  blueprintSubN: 'choices so far, your plan on one page',
  recallTitle: 'Today’s question',
  recallEmptyH1: 'Today’s question',
  recallEmpty: 'Finish your first lesson and a question will wait here each day.',
  recallKeeps: 'Coming back to a question keeps what you learned.',
  weekQuiz: 'Unit {n} quiz',
  reviewReady: 'Your scan review is ready',
  reviewReadyPlan: 'Your scan review and plan are ready',
  deptLater: 'Later in your course',
  unitFrom: 'Unit {n}',
  goal_v_pillar: 'Play a lesson on a new topic this week.',
  goal_v_path: 'Start the topic you have not started yet.',
  goal_v_single: 'Try one of the single lessons on a topic.',
  collExplained: 'Lessons you explained in your own words.',
  collPillars: 'Topics you have learned from.',
  lightsNonePath: '{title}: your first lesson lights the first lamp',
  lampNone: 'Your first lesson lights the first lamp',
  reviewHead: 'Your scan review',
  popUntil: 'Open until {time}',
  popWhy: 'From lessons you’ve already finished. Double blooms while it is open.',
});

/** Where each word is read (the review table). Not imported by the member app. */
export const CLEAR_WHERE = {
  tabLessons: 'Tab bar, side rail, back buttons',
  navCalm: 'Side rail (with channels on)',
  navReview: 'Side rail, Lessons screen row',
  navProgress: 'Side rail, Lessons screen row, Progress screen eyebrow',
  chanLine: 'Home: the line under Your channels',
  moreRow: 'Home: the last row',
  lessonsH1: 'Lessons screen title',
  lessonsSub: 'Lessons screen line',
  byTopic: 'Lessons screen, above the topic tiles',
  progressRowSub: 'Lessons row under Your progress',
  practicesRow: 'Lessons row, the practices page title',
  practicesRowSub: 'Lessons row line, the practices page line',
  sevenDay: 'Topic tile tag',
  pathDone: 'Topic tile tag, seven days screen',
  meaningTile: 'Topic tile line',
  tryQuest: 'Topic screen eyebrow',
  startQuest: 'Topic screen button',
  pathCardHead: 'Topic screen, the seven days card',
  startPath: 'Topic screen button',
  continuePath: 'Topic screen button',
  backToPillar: 'A single lesson, the way back',
  pathLine: 'Seven days screen, the line under the name',
  questMin: 'Seven days screen rows',
  backToPaths: 'Seven days screen',
  questOf: 'Player header',
  closeQuest: 'Player close button',
  startFull: 'Check in button',
  startLite: 'Check in button',
  swapToFull: 'Player',
  complete: 'Celebrate',
  upNextQuest: 'Celebrate',
  backToPath: 'Celebrate, the way back',
  nextQuest: 'Home week card, weekly recap',
  doneQuest: 'Home finish line',
  hlQuest: 'Home highlights',
  weekLearnOne: 'Week card',
  weekLearnMany: 'Week card',
  weekPracticeOne: 'Week card',
  weekPracticeMany: 'Week card',
  explainedSmall: 'Progress tiles',
  triedSmall: 'Progress tiles',
  learnedNone: 'Weekly recap',
  nextPaths: 'Weekly recap',
  mapLater: 'Map',
  recapHead: 'Home recap card',
  doorQuest: 'A full day, the other doors',
  fQuests: 'Progress so far',
  fTried: 'Progress so far',
  chQuests: 'Channel screen heading',
  chChallenges: 'Channel screen heading',
  chChallengesSub: 'Channel screen, under that heading',
  todayHere: 'Channel screen',
  bedHead: 'Profile',
  readAloudSub: 'Profile',
  soundsLine: 'Setup, the sounds step',
  unlockLine: 'Lesson finish',
  journeyEye: 'Home and Lessons progress card, the achievement screen back button',
  soFar: 'Progress screen',
  pathNoEnd: 'Progress screen',
  marksHeading: 'Progress screen, the badges card',
  ringLearn: 'Progress: the three things today',
  ringRecall: 'Progress: the three things today, a question waiting',
  blueprintSub: 'Progress: the Blueprint row',
  blueprintSubN: 'Progress: the Blueprint row, after the count',
  recallTitle: 'The question card header',
  recallEmptyH1: 'The question card, none yet',
  recallEmpty: 'The question card, none yet',
  recallKeeps: 'The question card, after a right answer',
  weekQuiz: 'Quiz history',
  reviewReady: 'Home: the scan review row',
  reviewReadyPlan: 'Home: the scan review row, with a plan',
  deptLater: 'Topic tile line',
  unitFrom: 'Quiz question and Today’s question: where it comes from',
  goal_v_pillar: 'Today’s small goals',
  goal_v_path: 'Today’s small goals',
  goal_v_single: 'Today’s small goals',
  collExplained: 'Progress: under Said in my words',
  collPillars: 'Progress: under Wide garden',
  lightsNonePath: 'Garden: the line under a topic with no lamp lit',
  lampNone: 'Garden: the picture before anything is lit',
  reviewHead: 'Scan review page: the title and the eyebrow',
  popUntil: 'Pop quiz card on Home and Quizzes, under the title',
  popWhy: 'Quizzes screen, the pop quiz card',
};

/** What she read before each word (the review table and the retired word tests). Not imported by the member app. An empty before is a new line. */
export const CLEAR_BEFORE = {
  tabLessons: 'Paths',
  navCalm: 'Mind & Spirit',
  navReview: 'Bi-weekly review',
  navProgress: 'My journey',
  chanLine: '5 small steps in the last 30 days. Growing.',
  moreRow: 'More: your numbers, quizzes and your week',
  lessonsH1: 'Your paths',
  lessonsSub: 'Everything you’re learning, in one place.',
  byTopic: '',
  progressRowSub: 'Your achievements, your path, no end',
  practicesRow: 'Practices and challenges',
  practicesRowSub: 'Water, sleep, food, moving and more',
  sevenDay: '7 day path',
  pathDone: 'Path complete',
  meaningTile: 'Blueprint, sanctuary, journey',
  tryQuest: 'Try a quest',
  startQuest: 'Start the quest',
  pathCardHead: 'Six short quests and a review day',
  startPath: 'Start the path',
  continuePath: 'Continue the path',
  backToPillar: 'Back to the pillar',
  pathLine: 'Six short quests and a review day. About 10 minutes each.',
  questMin: 'Quest {n} · {m} min',
  backToPaths: 'Back to Paths',
  questOf: 'Quest {n} of {of}',
  closeQuest: 'Close quest',
  startFull: 'Start the quest',
  startLite: 'Start the lighter quest',
  swapToFull: 'Do the full quest instead',
  complete: 'Quest complete',
  upNextQuest: 'Up next · Quest {n}: {title}',
  backToPath: 'Back to the path',
  nextQuest: 'Next quest: {title}',
  doneQuest: 'Next quest, tomorrow: {title}.',
  hlQuest: 'That is your {ord} quest this week.',
  weekLearnOne: 'quest explained',
  weekLearnMany: 'quests explained',
  weekPracticeOne: 'action tried',
  weekPracticeMany: 'actions tried',
  explainedSmall: 'quests explained',
  triedSmall: 'actions tried',
  learnedNone: 'No quests this week. The path waits, no rush.',
  nextPaths: 'Pick any quest from Paths.',
  mapLater: 'Quests for this pillar come later. {n} are planned.',
  recapHead: 'Your week of quests is ready.',
  doorQuest: 'A quest to play',
  fQuests: 'Quests finished',
  fTried: 'Actions tried',
  chQuests: 'Quests on this channel',
  chChallenges: 'Challenges',
  chChallengesSub: '',
  todayHere: 'Today here',
  bedHead: 'A sound under your quests',
  readAloudSub: 'A Read to me button on quest screens.',
  soundsLine: 'Quests can play soft water, wind and birds.',
  unlockLine: 'A quest opened early for you: {title}.',
  journeyEye: 'Your journey',
  soFar: 'Your journey so far',
  pathNoEnd: 'This path has no end',
  marksHeading: 'Marks',
  ringLearn: 'A lesson or a recall card',
  ringRecall: 'One recall card from past lessons',
  blueprintSub: 'Your one-page plan fills in as you build',
  blueprintSubN: 'choices so far, your one-page plan',
  recallTitle: 'Daily recall',
  recallEmptyH1: 'Recall cards',
  recallEmpty: 'Finish your first lesson and a daily recall card will appear here.',
  recallKeeps: 'Spaced recall keeps what you learned for good.',
  weekQuiz: 'Week {n} quiz',
  reviewReady: 'Your bi-weekly review is ready',
  reviewReadyPlan: 'Your bi-weekly review and plan are ready',
  deptLater: 'Later on your path',
  unitFrom: 'Week {n}',
  goal_v_pillar: 'Try a quest on a new pillar this week.',
  goal_v_path: 'Start the path you have not started yet.',
  goal_v_single: 'Try one of the single quests on a pillar.',
  collExplained: 'Quests you explained in your own words.',
  collPillars: 'Pillars you have learned from.',
  lightsNonePath: '{title}: your first quest lights the first lamp',
  lampNone: 'Your first quest lights the first lamp',
  reviewHead: 'Your bi-weekly review',
  popUntil: 'Fades in 23h 58m',
  popWhy: 'From lessons you’ve already finished. Double blooms if you play before it fades.',
};

/** Every row: the key the code asks for, where she reads it, the word before and the word after. */
export const CLEAR_ROWS = () => Object.keys(CLEAR_WORDS).map((key) => ({ key, where: CLEAR_WHERE[key] ?? '', before: CLEAR_BEFORE[key] ?? '', after: CLEAR_WORDS[key] }));

/** Every word she reads with the flag on, for the review id, the content check and the copy test. */
export const clearWordsStrings = () => Object.values(CLEAR_WORDS).filter(Boolean);

/** Fills {name} holes. */
export const fillCW = (s, vars) => s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''));

/** The nouns retired from the chrome: a screen with the flag on never shows one of these outside approved content (the tests sweep for them). */
export const RETIRED_WORDS = Object.freeze(['quest', 'quests', 'Paths', 'pillar', 'pillars', 'Daily recall', 'Bi-weekly', 'My journey', 'Next mark', 'Next benchmark']);
